import {
  Component,
  OnInit,
  signal,
  inject,
  OnDestroy,
  computed,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { TableModule } from 'primeng/table';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

import {
  PurchaseOrderDiscrepancy,
  PurchaseOrderService,
} from '../services/purchase-order.service';
import { PurchaseOrderConfigService } from '@app/feature/configuration/order/purchase-order-config/services/purchase-order-config.service';
import {
  AddFormComponent,
  FormField,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import {
  applyPurchaseOrderConfigToFields,
  getPurchaseOrderStatusField,
  withModeLabels,
  mapPurchaseOrderItemsToDetailList,
} from '../po.helper';
import { parsePositiveIntRouteParam } from '@shared/utils/route-param.util';
import {
  PURCHASE_ORDER_ADD_FORM_FIELDS,
  PURCHASE_ORDER_ADD_FORM_FIELDS_PR,
  PURCHASE_ORDER_DETAIL_TABLE_SECTION_VIEW,
  PurchaseOrderDetailItem,
} from '../purchase-order-add/purchase-order-add.config';

@Component({
  selector: 'app-purchase-order-view',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AddFormComponent,
    ButtonModule,
    ToastModule,
    DialogModule,
    TextareaModule,
    TableModule,
    FormsModule,
  ],
  providers: [MessageService],
  templateUrl: './purchase-order-view.component.html',
  styleUrl: './purchase-order-view.component.css',
})
export class PurchaseOrderViewComponent implements OnInit, OnDestroy {
  private static readonly PURCHASE_ORDER_LIST_ROUTE =
    '/procurement/purchase-order';
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly messageService = inject(MessageService);
  private readonly purchaseOrderService = inject(PurchaseOrderService);
  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly purchaseOrderConfigService = inject(
    PurchaseOrderConfigService,
  );
  private readonly destroy$ = new Subject<void>();

  // Form configuration
  formFields = signal<FormField[]>([]);
  headerActions = signal<ActionButton[]>([]);

  // Data
  detailList = signal<PurchaseOrderDetailItem[]>([]);
  loading = signal(false);
  currentFormData: any = null; // Changed from signal to regular property
  purchaseOrderId: string | null = null;

  // Mode detection
  isPurchaseRequestMode = signal(false);
  isNormalMode = signal(false);

  // Receipt discrepancies (US-B3-04)
  discrepancies = signal<PurchaseOrderDiscrepancy[]>([]);

  // Close-short dialog
  showCloseShortDialog = signal(false);
  closeShortReason = '';
  closeShortSubmitting = signal(false);

  // Computed table sections: view-only columns (no input, all read-only)
  tableSections = computed(() => {
    const viewSection = PURCHASE_ORDER_DETAIL_TABLE_SECTION_VIEW;
    const readOnlyColumns = (viewSection.columns || []).flatMap((col: any) => {
      const base = { ...col, editable: false };
      const column =
        col.type === 'inputNumber' ? { ...base, type: 'number' as const } : base;
      // Insert "Received" right after the ordered quantity column
      if (col.prop === 'quantity') {
        return [
          column,
          {
            label: 'purchase_order.view.discrepancies.received_qty',
            prop: 'receivedQuantity',
            width: 120,
            align: 'right' as const,
            type: 'number' as const,
          },
        ];
      }
      return [column];
    });
    return [
      {
        ...viewSection,
        data: this.detailList(),
        columns: readOnlyColumns,
      },
    ];
  });

  ngOnInit(): void {
    this.initializeForm();
    this.loadPurchaseOrder();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadAndApplyPurchaseOrderConfig(): void {
    const config =
      this.purchaseOrderConfigService.getPurchaseOrderFieldsFromStorage();
    if (config && config.length > 0) {
      this.formFields.set(
        applyPurchaseOrderConfigToFields(config, this.formFields()),
      );
      this.cdr.detectChanges();
      return;
    }
    this.purchaseOrderConfigService.getPurchaseOrderFields().subscribe({
      next: (fields) => {
        this.formFields.set(
          applyPurchaseOrderConfigToFields(fields, this.formFields()),
        );
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading purchase order config:', err);
      },
    });
  }

  private initializeForm(): void {
    const idNum = parsePositiveIntRouteParam(
      this.route.snapshot.paramMap.get('id'),
    );
    this.purchaseOrderId = idNum != null ? String(idNum) : null;
    if (this.purchaseOrderId == null) {
      this.router.navigate([
        PurchaseOrderViewComponent.PURCHASE_ORDER_LIST_ROUTE,
      ]);
      return;
    }

    // Initialize with default form fields - will be updated after loading data
    // Set header actions for view (only back button)
    this.headerActions.set([
      {
        label: 'purchase_order.view.actions.back',
        action: 'back',
        outlined: true,
        severity: 'secondary' as const,
      },
    ]);
  }

  private loadPurchaseOrder(): void {
    if (this.purchaseOrderId) {
      this.loading.set(true);

      // Call the actual API to get purchase order details
      this.purchaseOrderService
        .getPurchaseOrderById(this.purchaseOrderId)
        .subscribe({
          next: (purchaseOrder) => {
            if (purchaseOrder) {
              // Set form data first
              this.currentFormData = {
                series: purchaseOrder.series || '',
                vendorCode: purchaseOrder.vendorCode || '',
                deliveryDate: this.parseDateToMidnight(
                  purchaseOrder.deliveryDate,
                ),
                description: purchaseOrder.description || '',
                purchaseRequest:
                  purchaseOrder.purchaseRequest &&
                  typeof purchaseOrder.purchaseRequest === 'object' &&
                  'series' in purchaseOrder.purchaseRequest &&
                  'title' in purchaseOrder.purchaseRequest
                    ? `${
                        (purchaseOrder.purchaseRequest as { series: string })
                          .series
                      } - ${
                        (purchaseOrder.purchaseRequest as { title: string })
                          .title
                      }`
                    : '',
                status: purchaseOrder.status || 'PENDING',
              };

              // Detect mode based on purchaseRequest field
              if (
                purchaseOrder.purchaseRequest &&
                purchaseOrder.purchaseRequest !== ''
              ) {
                this.isPurchaseRequestMode.set(true);
                this.isNormalMode.set(false);
                this.initializeFormForPurchaseRequestMode(purchaseOrder);
              } else {
                this.isPurchaseRequestMode.set(false);
                this.isNormalMode.set(true);
                this.initializeFormForNormalMode(purchaseOrder);
              }

              // Populate vendor options with current vendor after form is initialized
              // If vendor data is not available, convert field to text to display vendorCode
              if (purchaseOrder.vendor?.vendorName) {
                this.populateVendorOptions(purchaseOrder.vendor);
              } else if (purchaseOrder.vendorCode) {
                // Fallback: convert vendor field to text to display vendorCode
                this.convertVendorFieldToText(purchaseOrder.vendorCode);
              }

              // Set detail items from API response
              if (purchaseOrder.items && purchaseOrder.items.length > 0) {
                this.detailList.set(
                  mapPurchaseOrderItemsToDetailList(purchaseOrder.items),
                );

                // Force change detection to ensure table updates
                this.cdr.detectChanges();
              } else {
                this.detailList.set([]);
              }

              // Load receipt discrepancies + surface the close-short action
              this.loadDiscrepancies();
              this.updateHeaderActionsForStatus(purchaseOrder.status);
            }
            this.loading.set(false);
          },
          error: (error) => {
            console.error('Error loading purchase order:', error);
            this.loading.set(false);
          },
        });
    }
  }

  private initializeFormForPurchaseRequestMode(purchaseOrder: any): void {
    const viewStyle =
      'background-color: #f5f5f5; color: #666; cursor: not-allowed;';
    const dateStyle =
      'background-color: #F3F4F6 !important; color: #666 !important; cursor: not-allowed !important;';
    const prModeFormFields = PURCHASE_ORDER_ADD_FORM_FIELDS_PR.map(
      (field: any) => {
        const base = {
          ...withModeLabels(field, 'view'),
          disabled: true,
          readonly: true,
          style: viewStyle,
        };
        if (field.controlName === 'purchaseRequest') {
          return { ...base, type: 'text' as const };
        }
        if (field.controlName === 'vendorCode') {
          return {
            ...base,
            label: 'purchase_order.view.fields.vendor',
            type: 'searchableDropdown' as const,
            options: [],
            showClear: false,
          };
        }
        if (field.type === 'date') {
          return { ...base, style: dateStyle };
        }
        return base;
      },
    );

    const statusField = getPurchaseOrderStatusField(
      'view',
      purchaseOrder.status,
    );
    this.formFields.set([statusField, ...prModeFormFields]);

    // Populate purchase request options with current data
    this.populatePurchaseRequestOptions(purchaseOrder);

    // Apply config after form is fully initialized
    this.loadAndApplyPurchaseOrderConfig();

    // Trigger change detection to ensure form updates
    this.cdr.detectChanges();
  }

  private initializeFormForNormalMode(purchaseOrder: any): void {
    const viewStyle =
      'background-color: #f5f5f5; color: #666; cursor: not-allowed;';
    const dateStyle =
      'background-color: #F3F4F6 !important; color: #666 !important; cursor: not-allowed !important;';
    const normalModeFormFields = PURCHASE_ORDER_ADD_FORM_FIELDS.filter(
      (field: any) => field.controlName !== 'purchaseRequest',
    ).map((field: any) => {
      const base = {
        ...withModeLabels(field, 'view'),
        disabled: true,
        readonly: true,
        style: viewStyle,
      };
      if (field.controlName === 'vendorCode') {
        return {
          ...base,
          label: 'purchase_order.view.fields.vendor',
          type: 'searchableDropdown' as const,
          options: [],
          showClear: false,
        };
      }
      if (field.type === 'date') {
        return { ...base, style: dateStyle };
      }
      return base;
    });

    const statusField = getPurchaseOrderStatusField(
      'view',
      purchaseOrder.status,
    );
    this.formFields.set([statusField, ...normalModeFormFields]);

    // Apply config after form is fully initialized
    this.loadAndApplyPurchaseOrderConfig();

    // Trigger change detection to ensure form updates
    this.cdr.detectChanges();
  }

  // Populate purchase request options with current data
  private populatePurchaseRequestOptions(purchaseOrder: any): void {
    if (
      purchaseOrder.purchaseRequest &&
      typeof purchaseOrder.purchaseRequest === 'object' &&
      'id' in purchaseOrder.purchaseRequest
    ) {
      const pr = purchaseOrder.purchaseRequest as {
        id: string | number;
        series?: string;
        title?: string;
      };
      // Update the purchaseRequest field options with current data
      const currentFormFields = this.formFields();
      const updatedFormFields = currentFormFields.map((field: any) => {
        if (field.controlName === 'purchaseRequest') {
          return {
            ...field,
            options: [
              {
                label:
                  pr.series && pr.title ? `${pr.series} - ${pr.title}` : '',
                value: pr.id,
              },
            ],
          };
        }
        return field;
      });
      this.formFields.set(updatedFormFields);
    }
  }

  // Populate vendor options with current vendor data
  private populateVendorOptions(vendor: any): void {
    if (vendor?.vendorCode && vendor?.vendorName) {
      const currentFormFields = this.formFields();
      const updatedFormFields = currentFormFields.map((field: any) => {
        if (field.controlName === 'vendorCode') {
          return {
            ...field,
            options: [
              {
                label: vendor.vendorName,
                value: vendor.vendorCode,
              },
            ],
          };
        }
        return field;
      });
      this.formFields.set(updatedFormFields);

      // Update form data to use vendorCode (which will display vendorName via dropdown)
      if (this.currentFormData) {
        this.currentFormData.vendorCode = vendor.vendorCode;
      }
    }
  }

  // Convert vendor field to text when vendor data is not available
  private convertVendorFieldToText(vendorCode: string): void {
    const currentFormFields = this.formFields();
    const updatedFormFields = currentFormFields.map((field: any) => {
      if (field.controlName === 'vendorCode') {
        return {
          ...field,
          type: 'text' as const,
          disabled: true,
          readonly: true,
          style: 'background-color: #f5f5f5; color: #666; cursor: not-allowed;',
        };
      }
      return field;
    });
    this.formFields.set(updatedFormFields);

    // Update form data to display vendorCode as text
    if (this.currentFormData) {
      this.currentFormData.vendorCode = vendorCode;
    }
  }

  // Parse date to midnight (same as edit component)
  private parseDateToMidnight(dateString: string): Date {
    if (!dateString) return new Date();

    // Parse the date and set time to midnight (00:00:00) to avoid timezone issues
    const date = new Date(dateString);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  // Handle form value changes (not needed for view)
  onFormValueChanges(formData: any): void {
    // No action needed for view mode
  }

  // Handle form actions (back / close-short)
  onFormAction(action: string): void {
    switch (action) {
      case 'back':
        this.onBack();
        break;
      case 'closeShort':
        this.openCloseShortDialog();
        break;
    }
  }

  /** Fetch discrepancies recorded by the warehouse for this order. */
  private loadDiscrepancies(): void {
    const id = this.purchaseOrderId ? Number(this.purchaseOrderId) : null;
    if (id == null) {
      return;
    }
    this.purchaseOrderService.getDiscrepancies(id).subscribe({
      next: (rows) => this.discrepancies.set(rows || []),
      error: (err) => console.error('Error loading discrepancies:', err),
    });
  }

  /** Add a "Close short" header action while the order can still be closed short. */
  private updateHeaderActionsForStatus(status: string | undefined): void {
    const closable = ['PARTIALLY_RECEIVED', 'VENDOR_CONFIRMED', 'SENT'].includes(
      status || '',
    );
    const actions = [
      {
        label: 'purchase_order.view.actions.back',
        action: 'back',
        outlined: true,
        severity: 'secondary' as const,
      },
    ];
    if (closable) {
      actions.unshift({
        label: 'purchase_order.view.actions.close_short',
        action: 'closeShort',
        outlined: false,
        severity: 'warn' as const,
      } as any);
    }
    this.headerActions.set(actions);
  }

  openCloseShortDialog(): void {
    this.closeShortReason = '';
    this.showCloseShortDialog.set(true);
  }

  confirmCloseShort(): void {
    const reason = (this.closeShortReason || '').trim();
    if (!reason) {
      return;
    }
    const id = this.purchaseOrderId ? Number(this.purchaseOrderId) : null;
    if (id == null) {
      return;
    }
    this.closeShortSubmitting.set(true);
    this.purchaseOrderService.closeShort(id, reason).subscribe({
      next: () => {
        this.closeShortSubmitting.set(false);
        this.showCloseShortDialog.set(false);
        this.purchaseOrderService.showSuccessMessage(
          'purchase_order.view.close_short.success',
        );
        this.loadPurchaseOrder();
      },
      error: () => {
        this.closeShortSubmitting.set(false);
        this.purchaseOrderService.showErrorMessage(
          'purchase_order.view.close_short.error',
        );
      },
    });
  }

  // Handle table actions (none for view mode)
  onTableAction(event: { action: string; data?: any }): void {
    // No actions allowed in view mode
  }

  // Go back to purchase order list
  onBack(): void {
    this.router.navigate([
      PurchaseOrderViewComponent.PURCHASE_ORDER_LIST_ROUTE,
    ]);
  }

  // Format currency for display
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(value);
  }

  // Calculate total value
  getTotalValue(): number {
    return this.detailList().reduce(
      (total, item) => total + item.totalValue,
      0,
    );
  }
}
